import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowUpRight, MapPin } from "lucide-react";
import { BRAND, MEDIA, STATS } from "@/data/content";

const HEADLINE_LINES = [
  <>We build the brands</>,
  <><span className="text-text-2">that build</span> <span className="text-accent">Sacramento.</span></>,
];

export const Hero = () => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const bgY = useTransform(scrollYProgress, [0, 1], ["0%", "22%"]);
  const contentY = useTransform(scrollYProgress, [0, 1], ["0%", "-12%"]);
  const fade = useTransform(scrollYProgress, [0, 0.8], [1, 0]);

  return (
    <section ref={ref} id="top" data-testid="hero-section" className="relative min-h-screen flex items-end overflow-hidden grain">
      <motion.div style={{ y: bgY }} className="absolute inset-0 z-0 scale-125 will-change-transform">
        <img src={MEDIA.heroNetwork} alt="Emergent network" className="w-full h-full object-cover" />
        <div className="absolute inset-0 bg-ink/85" />
        <div className="absolute inset-0 bg-gradient-to-t from-ink via-ink/60 to-transparent" />
      </motion.div>

      <motion.div style={{ y: contentY, opacity: fade }} className="container-x relative z-10 pb-20 pt-32 w-full">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          className="flex items-center gap-2 mb-8"
        >
          <span className="w-1.5 h-1.5 rounded-full bg-accent animate-pulse" />
          <span className="mono-label text-accent">{BRAND.tagline}</span>
        </motion.div>

        <h1 className="font-head font-black tracking-tighter text-5xl sm:text-7xl lg:text-8xl leading-[0.95] max-w-5xl text-balance">
          {HEADLINE_LINES.map((line, i) => (
            <span key={i} className="block overflow-hidden pb-[0.08em]">
              <motion.span
                initial={{ y: "115%" }}
                animate={{ y: 0 }}
                transition={{ duration: 0.95, delay: 0.15 + i * 0.13, ease: [0.16, 1, 0.3, 1] }}
                className="block"
              >
                {line}
              </motion.span>
            </span>
          ))}
        </h1>

        <motion.p
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.3 }}
          className="mt-8 max-w-xl text-lg text-text-2 leading-relaxed"
        >
          Web design, branding, SEO, and social campaigns — engineered to grow local
          businesses. Founded in 1998, we bring 25+ years of experience as your city's
          official Emergent Technologies partner.
        </motion.p>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.45 }}
          className="mt-10 flex flex-wrap items-center gap-4"
        >
          <a
            href="#contact"
            data-testid="hero-cta-primary"
            className="inline-flex items-center gap-2 bg-accent text-ink font-head font-bold px-7 py-4 hover:bg-white transition-colors group"
          >
            Start your project
            <ArrowUpRight className="w-5 h-5 group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-transform" />
          </a>
          <a
            href="#services"
            data-testid="hero-cta-secondary"
            className="inline-flex items-center gap-2 border border-line text-white font-head font-semibold px-7 py-4 hover:border-accent hover:text-accent transition-colors"
          >
            View services
          </a>
          <span className="inline-flex items-center gap-2 text-text-2 text-sm ml-2">
            <MapPin className="w-4 h-4 text-accent" /> {BRAND.address}
          </span>
        </motion.div>

        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.6, delay: 0.6 }}
          className="mt-16 grid grid-cols-2 md:grid-cols-4 border-t border-line"
        >
          {STATS.map((s) => (
            <div key={s.label} className="py-6 pr-6 border-r border-line last:border-r-0">
              <div className="font-head font-black text-3xl sm:text-4xl text-white">{s.value}</div>
              <div className="mono-label text-text-2 mt-1">{s.label}</div>
            </div>
          ))}
        </motion.div>
      </motion.div>
    </section>
  );
};
